import React, { useState } from 'react';
import {
  Paper,
  Title,
  Text,
  Group,
  Stack,
  Button,
  Badge,
  ActionIcon,
  Divider,
  Modal,
  Menu,
  Box,
  LoadingOverlay,
} from '@mantine/core';
import { Calendar } from '@mantine/dates';
import {
  IconPlus,
  IconPencil,
  IconTrash,
  IconCheck,
  IconX,
  IconClockHour4,
  IconDotsVertical,
} from '@tabler/icons-react';
import dayjs from 'dayjs';

// Interfaces
interface TimeSlot {
  id: string;
  start_time: string;
  end_time: string;
  available: boolean;
  status: 'available' | 'pending' | 'booked' | 'cancelled' | 'completed' | 'no-show';
  booking?: {
    id: string;
    status: string;
  };
}

interface TimeSlotCalendarProps {
  timeSlots: TimeSlot[];
  isLoading: boolean;
  onAddTimeSlots: () => void;
  onEditTimeSlot: (timeSlot: TimeSlot) => void;
  onDeleteTimeSlot: (timeSlotId: string) => void;
  onUpdateStatus: (timeSlotId: string, status: TimeSlot['status']) => void;
}

// Helper functions
const getStatusColor = (status: string) => {
  const statusColors: Record<string, string> = {
    available: 'green',
    pending: 'yellow',
    booked: 'blue',
    cancelled: 'gray',
    completed: 'teal',
    'no-show': 'red',
  };
  return statusColors[status] || 'gray';
};

const formatTime = (dateString: string) => {
  return dayjs(dateString).format('h:mm A');
};

// Component
export const TimeSlotCalendar: React.FC<TimeSlotCalendarProps> = ({
  timeSlots,
  isLoading,
  onAddTimeSlots,
  onEditTimeSlot,
  onDeleteTimeSlot,
  onUpdateStatus,
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(new Date());
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [slotToDelete, setSlotToDelete] = useState<TimeSlot | null>(null);

  // Group time slots by date
  const timeSlotsByDate = React.useMemo(() => {
    const grouped: Record<string, TimeSlot[]> = {};

    timeSlots.forEach((slot) => {
      const date = dayjs(slot.start_time).format('YYYY-MM-DD');
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(slot);
    });

    return grouped;
  }, [timeSlots]);

  // Get dates that have time slots
  const datesWithTimeSlots = Object.keys(timeSlotsByDate);

  // Handle delete confirmation
  const openDeleteModal = (slot: TimeSlot) => {
    setSlotToDelete(slot);
    setDeleteModalOpen(true);
  };

  const handleDelete = () => {
    if (slotToDelete) {
      onDeleteTimeSlot(slotToDelete.id);
    }
    setDeleteModalOpen(false);
    setSlotToDelete(null);
  };

  // Get time slots for selected date
  const slotsForSelectedDate = selectedDate ? timeSlotsByDate[dayjs(selectedDate).format('YYYY-MM-DD')] || [] : [];

  // Sort time slots by start time
  const sortedSlotsForSelectedDate = [...slotsForSelectedDate].sort(
    (a, b) => new Date(a.start_time).getTime() - new Date(b.start_time).getTime(),
  );

  // Update time slot status
  const handleStatusChange = (timeSlot: TimeSlot, newStatus: TimeSlot['status']) => {
    onUpdateStatus(timeSlot.id, newStatus);
  };

  return (
    <Paper p="md" pos="relative">
      <LoadingOverlay visible={isLoading} />

      <Group justify="apart" mb="md">
        <Title order={3}>Manage Time Slots</Title>
        <Button leftSection={<IconPlus size={16} />} onClick={onAddTimeSlots}>
          Add Time Slots
        </Button>
      </Group>

      <Group align="flex-start" gap="xl">
        <Box style={{ flexBasis: '300px', flexShrink: 0 }}>
          <Calendar
            value={selectedDate}
            onChange={setSelectedDate}
            size="md"
            fullWidth
            styles={(theme) => ({
              day: {
                '&[data-has-slots]': {
                  backgroundColor: theme.colors.blue[0],
                  borderRadius: '4px',
                  fontWeight: 'bold',
                },
              },
            })}
            renderDay={(date) => {
              const hasSlots = datesWithTimeSlots.includes(dayjs(date).format('YYYY-MM-DD'));
              return (
                <div data-has-slots={hasSlots || undefined}>
                  <Text size="sm">{date.getDate()}</Text>
                  {hasSlots && (
                    <Box style={{ position: 'absolute', bottom: '3px', left: '50%', transform: 'translateX(-50%)' }}>
                      <Box
                        style={{ width: '4px', height: '4px', borderRadius: '50%', backgroundColor: 'currentColor' }}
                      />
                    </Box>
                  )}
                </div>
              );
            }}
          />
        </Box>

        <Stack gap="md" style={{ flex: 1 }}>
          {selectedDate && (
            <>
              <Title order={4}>
                {dayjs(selectedDate).format('dddd, MMMM D, YYYY')}
                {sortedSlotsForSelectedDate.length > 0 && (
                  <Text span size="sm" ml="md" color="dimmed">
                    ({sortedSlotsForSelectedDate.length} time slots)
                  </Text>
                )}
              </Title>

              {sortedSlotsForSelectedDate.length === 0 ? (
                <Text color="dimmed" italic>
                  No time slots scheduled for this date
                </Text>
              ) : (
                sortedSlotsForSelectedDate.map((slot) => (
                  <Paper
                    key={slot.id}
                    shadow="xs"
                    withBorder
                    p="md"
                    style={{
                      borderLeft: `4px solid ${slot.status ? getStatusColor(slot.status) : 'gray'}`,
                    }}
                  >
                    <Group position="apart">
                      <Group>
                        <IconClockHour4 size={20} />
                        <Text fw={500}>
                          {formatTime(slot.start_time)} - {formatTime(slot.end_time)}
                        </Text>
                        <Badge color={getStatusColor(slot.status)}>
                          {slot.status.charAt(0).toUpperCase() + slot.status.slice(1)}
                        </Badge>
                      </Group>

                      <Group>
                        <Menu position="bottom-end" shadow="md">
                          <Menu.Target>
                            <ActionIcon>
                              <IconDotsVertical size={16} />
                            </ActionIcon>
                          </Menu.Target>

                          <Menu.Dropdown>
                            <Menu.Label>Status</Menu.Label>
                            <Menu.Item
                              icon={<IconCheck size={14} color="green" />}
                              onClick={() => handleStatusChange(slot, 'available')}
                              disabled={slot.status === 'available'}
                            >
                              Mark as Available
                            </Menu.Item>
                            <Menu.Item
                              icon={<IconCheck size={14} color="blue" />}
                              onClick={() => handleStatusChange(slot, 'completed')}
                              disabled={slot.status === 'completed'}
                            >
                              Mark as Completed
                            </Menu.Item>
                            <Menu.Item
                              icon={<IconX size={14} color="red" />}
                              onClick={() => handleStatusChange(slot, 'no-show')}
                              disabled={slot.status === 'no-show'}
                            >
                              Mark as No-Show
                            </Menu.Item>
                            <Menu.Item
                              icon={<IconX size={14} color="gray" />}
                              onClick={() => handleStatusChange(slot, 'cancelled')}
                              disabled={slot.status === 'cancelled'}
                            >
                              Mark as Cancelled
                            </Menu.Item>
                            <Divider />
                            <Menu.Item icon={<IconPencil size={14} />} onClick={() => onEditTimeSlot(slot)}>
                              Edit
                            </Menu.Item>
                            <Menu.Item icon={<IconTrash size={14} />} color="red" onClick={() => openDeleteModal(slot)}>
                              Delete
                            </Menu.Item>
                          </Menu.Dropdown>
                        </Menu>
                      </Group>
                    </Group>
                  </Paper>
                ))
              )}
            </>
          )}
        </Stack>
      </Group>

      {/* Delete Confirmation Modal */}
      <Modal opened={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Delete Time Slot" centered>
        <Text size="sm">
          Are you sure you want to delete this time slot?
          {slotToDelete && (
            <Text weight={500} mt="xs">
              {dayjs(slotToDelete.start_time).format('MMMM D, YYYY')} from {formatTime(slotToDelete.start_time)} to{' '}
              {formatTime(slotToDelete.end_time)}
            </Text>
          )}
        </Text>

        <Group justify="right" mt="lg">
          <Button variant="outline" onClick={() => setDeleteModalOpen(false)}>
            Cancel
          </Button>
          <Button color="red" onClick={handleDelete}>
            Delete
          </Button>
        </Group>
      </Modal>
    </Paper>
  );
};
